import { useState, useRef, useCallback } from "react"
import { Paperclip, Cloud } from "lucide-react"
import {
  Badge,
  Dialog,
  DialogHeader,
  DialogTitle,
  Tooltip,
  TooltipTrigger,
  Separator,
} from "@f2e/ui/components/base"
import {
  TooltipContentPlus,
  DialogDraggableContent,
} from "@f2e/ui/components/base-plus"
import { cn } from "@f2e/ui/lib/utils"

import {
  FileUploadCollector,
  FileUploadCollectorHandle,
} from "./FileUploadCollector"
import { InlineFileUpload } from "./InlineFileUpload"
import { KnowledgeNodeDropdown } from "./KnowledgeNodeDropdown"
import BaseFileBadge from "./BaseFileBadge"
import { Workspace, ContextObj, FileAttachmentScope } from "@/types"
import { useTranslation } from "react-i18next"
import { useToast } from "@/hooks"
import { UploadingFile } from "./FileUploadTypes"
import {
  usePostAttachmentsFromContextObjectsApi,
  type KnowledgeNodeSchema,
} from "@/hooks/api"
import { useEffect, forwardRef, useImperativeHandle } from "react"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"

export interface AttachmentCollectorProps {
  className?: string
  workspace?: Workspace
  label?: string
  withIcon?: boolean
  disabled?: boolean
  canShowDialog?: boolean
  initialFiles?: UploadingFile[]
  maxAttachments?: number
  isEvaluationContext?: boolean
  allowDownload?: boolean
  hideFiles?: boolean // Hide file badges, only show icon
  onAttachmentsChange?: (
    files: UploadingFile[],
    contextObjects?: ContextObj[]
  ) => void
  onOpenChange?: (open: boolean) => void
  onUnselect?: (file: UploadingFile) => void
}

export interface AttachmentCollectorHandle {
  getAllAttachmentIds: () => string[]
}

const AttachmentCollector = forwardRef<
  AttachmentCollectorHandle,
  AttachmentCollectorProps
>(
  (
    {
      className,
      workspace,
      label,
      withIcon = true,
      canShowDialog = true,
      initialFiles = [],
      maxAttachments = 5,
      isEvaluationContext = false,
      allowDownload = false,
      hideFiles = false,
      onAttachmentsChange,
      onOpenChange,
      onUnselect,
    },
    ref
  ) => {
    const { t } = useTranslation()
    const { alertToast } = useToast()
    const [show, setShow] = useState(false)
    const [files, setFiles] = useState<UploadingFile[]>(initialFiles)

    const fileUploadRef = useRef<FileUploadCollectorHandle>(null)

    // Type guard to ensure files have UUIDs
    const hasUuid = (
      file: UploadingFile
    ): file is UploadingFile & { uuid: string } => {
      return typeof file.uuid === "string" && file.uuid.trim() !== ""
    }

    useImperativeHandle(ref, () => ({
      getAllAttachmentIds: () => {
        const uploadedFileIds = files
          .filter((file) => file.status === "uploaded" && hasUuid(file))
          .map((file) => file.uuid as string)
        // Return unique IDs only
        return Array.from(new Set(uploadedFileIds))
      },
    }))

    const {
      error: attachmentError,
      fetchFn: createAttachmentsFromContextObjects,
    } = usePostAttachmentsFromContextObjectsApi()

    // Handle attachment creation errors from the hook
    useEffect(() => {
      if (attachmentError) {
        console.error("Attachment creation error:", attachmentError)
        alertToast({
          title: t("common.attachment.error.title"),
          description: t("common.attachment.error.contextObjects"),
        })
      }
    }, [attachmentError, alertToast, t])

    useEffect(() => {
      // Only update files if initialFiles has actually changed
      // This prevents unnecessary resets that could cause duplication
      const currentFileIds = files
        .filter(hasUuid)
        .map((f) => f.uuid)
        .sort()
      const newFileIds = initialFiles
        .filter(hasUuid)
        .map((f) => f.uuid)
        .sort()
      if (JSON.stringify(currentFileIds) !== JSON.stringify(newFileIds)) {
        setFiles(initialFiles)
      }
    }, [initialFiles, files])

    const hasReachedLimit = files.length >= maxAttachments

    const handleOpenChange = (open: boolean) => {
      if (!open) {
        setShow(false)
      }
      onOpenChange?.(open)
    }

    const openDialog = useCallback(() => {
      if (!canShowDialog) return
      setShow(true)
      onOpenChange?.(true)
    }, [canShowDialog, onOpenChange])

    const handleClick = useCallback(
      (e?: React.MouseEvent<HTMLElement>) => {
        e?.preventDefault()
        openDialog()
      },
      [openDialog]
    )

    const handleFilesChange = useCallback(
      (newFiles: UploadingFile[]) => {
        setFiles(newFiles)
        onAttachmentsChange?.(newFiles)
      },
      [onAttachmentsChange]
    )

    const handleRemoveFile = useCallback(
      (fileToRemove: UploadingFile) => {
        // Filter by UUID if available, otherwise fall back to file properties matching
        // This handles files in pending/error states that may not have a UUID yet
        const newFiles = files.filter((file) => {
          if (fileToRemove.uuid && file.uuid) {
            return file.uuid !== fileToRemove.uuid
          }
          // Fall back to matching by file properties for files without UUID
          return !(
            file.file.name === fileToRemove.file.name &&
            file.file.size === fileToRemove.file.size &&
            file.file.lastModified === fileToRemove.file.lastModified
          )
        })
        setFiles(newFiles)
        onAttachmentsChange?.(newFiles)
        onUnselect?.(fileToRemove)
      },
      [files, onAttachmentsChange, onUnselect]
    )

    const createAttachmentsFromContextObjectId = async (params: {
      contextObjectId: number
      displayName: string
      url?: string
    }) => {
      if (!workspace) {
        alertToast({
          title: t("common.attachment.error.title"),
          description: "Workspace is required",
        })
        return
      }

      try {
        const requestData = {
          context_object_ids: [params.contextObjectId],
          workspace_id: workspace.id,
        }

        await createAttachmentsFromContextObjects({
          jsonBody: requestData,
          callbackFn: (response) => {
            // Here use callbackFn because we want to keep the original displayName/url
            if (response?.data) {
              // Create synthetic files for all returned attachment IDs
              const newSyntheticFiles: UploadingFile[] =
                response.data.attachment_ids.map((attachmentId) => ({
                  file: new File([], params.displayName),
                  status: "uploaded" as const,
                  uuid: attachmentId,
                  url: params.url || "",
                  scope: FileAttachmentScope.Persistent,
                }))

              // Check for duplicates using UUIDs before adding to state
              const existingUuids = new Set(
                files.filter((f) => f.uuid).map((f) => f.uuid)
              )
              const uniqueNewFiles = newSyntheticFiles.filter(
                (file) => !existingUuids.has(file.uuid)
              )

              if (uniqueNewFiles.length === 0) {
                // All files already exist in state
                alertToast({
                  title: t("common.attachment.error.title"),
                  description: "This file is already attached",
                })
                return
              }
              const newFiles = [...files, ...uniqueNewFiles]
              setFiles(newFiles)
              onAttachmentsChange?.(newFiles)

              alertToast({
                title: t("common.attachment.success.title"),
                description: t("common.attachment.success.contextObjects", {
                  count:
                    response.data.created_count + response.data.existing_count,
                }),
              })
            }
          },
        })
      } catch (error) {
        console.error(
          "Failed to create attachments from context objects:",
          error
        )
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("common.attachment.error.title"),
            fallbackDescription: t("common.attachment.error.contextObjects"),
          }),
        })
      }
    }

    const handleKnowledgeNodeFileSelect = async (node: KnowledgeNodeSchema) => {
      if (!workspace) {
        alertToast({
          title: t("common.attachment.error.title"),
          description: "Workspace is required",
        })
        return
      }

      if (!node.snapshot_object_id) {
        alertToast({
          title: t("common.attachment.error.title"),
          description: "This file is not ready yet (no snapshot).",
        })
        return
      }

      return createAttachmentsFromContextObjectId({
        contextObjectId: node.snapshot_object_id,
        displayName: node.name,
        url: node.origin_url ?? "",
      })
    }

    // Render a single file badge (reusable function)
    const renderFileBadge = useCallback(
      (file: UploadingFile, idx: number, size: "sm" | "md" = "md") => {
        if (allowDownload) {
          return (
            <BaseFileBadge
              key={`file-${idx}`}
              name={file.file.name}
              type={file.file.type}
              url={file.url}
              index={idx}
              onRemove={() => handleRemoveFile(file)}
              showFileName
              height={size === "sm" ? "h-8" : "h-10"}
              iconSize={size}
              attachmentId={file.uuid}
            />
          )
        }

        return (
          <Badge
            key={`file-${idx}`}
            variant="secondary"
            className="flex items-center gap-1"
          >
            <Paperclip className="h-3 w-3" />
            {file.file.name}
            <button
              className="ml-1 rounded-full outline-hidden cursor-pointer"
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                handleRemoveFile(file)
              }}
            >
              <span className="h-3 w-3 text-muted-foreground hover:text-foreground text-xs cursor-pointer">
                ×
              </span>
            </button>
          </Badge>
        )
      },
      [allowDownload, handleRemoveFile]
    )

    return (
      <>
        <div
          className={cn(
            "inline-flex gap-1 flex-wrap items-center rounded-md p-1",
            className
          )}
        >
          {withIcon && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={handleClick}
                  className="flex items-center justify-center cursor-pointer hover:bg-muted/20 rounded-md p-0.5 transition-colors"
                  aria-label={t("common.attachment.tooltip")}
                >
                  <Paperclip className="size-5 text-muted-foreground" />
                  {files.length > 0 && (
                    <span className="ml-0.5 text-xs text-muted-foreground">
                      ({files.length})
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContentPlus side="top">
                {t("common.attachment.tooltip")}
              </TooltipContentPlus>
            </Tooltip>
          )}

          {!hideFiles &&
            files.map((file, idx) => {
              const badge = renderFileBadge(file, idx, "sm")
              return allowDownload ? (
                <div key={`file-${idx}`} onClick={(e) => e.stopPropagation()}>
                  {badge}
                </div>
              ) : (
                badge
              )
            })}
          {files.length === 0 && !hideFiles && (
            <span className="text-muted-foreground text-sm ml-1">{label}</span>
          )}
        </div>

        <Dialog open={show} onOpenChange={handleOpenChange}>
          {/* The purpose of using the primitive dialog is to prevent drag events propagating to the parent */}
          <DialogDraggableContent
            className={cn(
              "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[750px] max-h-[90vh] translate-x-[-50%] translate-y-[-50%] gap-4 border border-slate-200 bg-white p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg dark:border-slate-800 dark:bg-slate-950"
            )}
            aria-describedby={undefined}
            onDragEnter={(e) => e.stopPropagation()}
            onDragOver={(e) => e.stopPropagation()}
            onDragLeave={(e) => e.stopPropagation()}
          >
            <DialogHeader>
              <DialogTitle>{t("common.attachment.title")}</DialogTitle>
            </DialogHeader>

            <div className="space-y-6 overflow-y-auto">
              {hasReachedLimit && (
                <div className="bg-muted/50 border border-muted rounded-md p-3">
                  <p className="text-sm text-muted-foreground">
                    {t("common.attachment.limitReached", {
                      limit: maxAttachments,
                    })}
                  </p>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t("common.attachment.files")}
                  </h4>
                  <InlineFileUpload
                    onFilesSelected={async (newFiles) => {
                      if (fileUploadRef.current) {
                        await fileUploadRef.current.handleFiles(newFiles)
                      }
                    }}
                    disabled={!workspace || hasReachedLimit}
                  />
                </div>

                <Separator />

                <div>
                  <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <Cloud className="w-4 h-4" />
                    {t("common.attachment.selectFromCloud")}
                  </h4>
                  <div className="space-y-3">
                    <div className="text-sm text-muted-foreground">
                      {t("common.contextSourceDropdown.instructions")}
                    </div>
                    {workspace?.id && (
                      <KnowledgeNodeDropdown
                        workspaceId={workspace.id}
                        onFileSelect={handleKnowledgeNodeFileSelect}
                        disabled={hasReachedLimit}
                      />
                    )}
                  </div>
                </div>

                <div>
                  <FileUploadCollector
                    ref={fileUploadRef}
                    workspace={workspace}
                    className="hidden"
                    files={files}
                    onStateChange={handleFilesChange}
                    isEvaluationContext={isEvaluationContext}
                    maxFiles={maxAttachments}
                  ></FileUploadCollector>
                </div>
              </div>

              {files.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium">
                      {t("common.attachment.currentAttachments")} (
                      {files.length}/{maxAttachments})
                    </h4>

                    <div className="space-y-2">
                      <div className="flex gap-1 flex-wrap">
                        {files.map((file, idx) => renderFileBadge(file, idx))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </DialogDraggableContent>
        </Dialog>
      </>
    )
  }
)

AttachmentCollector.displayName = "AttachmentCollector"

export { AttachmentCollector }
export default AttachmentCollector
